import type { IColorScheme } from '@services/BrandingsService';
import type { IBrandingEntityValues } from '@interfaces/rooms';

export type BrandingMapColorsUnrealContext = {
  brandingId: string;
  brandingColors: IColorScheme;
  roomId?: string;
  mapId?: string;
};

/** Map palette changes — same action as spot edits, without bulk `spots` (UE expects `spot` per update). */
export const sendBrandingMapColorsUpdatedToUnreal = (
  sendMessageToUnreal: (message: unknown) => void,
  context: BrandingMapColorsUnrealContext,
) => {
  sendMessageToUnreal({
    action: 'SPOTS_UPDATED',
    brandingId: context.brandingId,
    brandingColors: context.brandingColors,
    ...(context.roomId ? { roomId: context.roomId } : {}),
    ...(context.mapId ? { mapId: context.mapId } : {}),
  });
};

type SendMessageToUnreal = (message: unknown) => void;

/**
 * Applies entity property overrides in UE.
 *
 * Per-entity replace: each entity sent ends up with exactly the properties
 * listed, a property left out is restored to what the level authored, and
 * entities not named are untouched — so callers send only what they edited.
 * There is no type field on the wire; UE takes the type from the target
 * UPROPERTY, which is why every value travels as a string.
 *
 * @param sendMessageToUnreal - Transport from `useMytaverse()`.
 * @param entities - Values to apply, keyed by entity then property.
 */
export const sendBrandingEntitiesUpdatedToUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  entities: IBrandingEntityValues,
) => {
  sendMessageToUnreal({
    action: 'BRANDING_UPDATED',
    entities,
  });
};

/**
 * Drops every override in UE, so the level's authored values show again.
 *
 * @param sendMessageToUnreal - Transport from `useMytaverse()`.
 */
export const sendClearBrandingToUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
) => {
  sendMessageToUnreal({
    action: 'CLEAR_BRANDING',
  });
};

export const teleportToSpotInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  ueIdentifier: string,
) => {
  sendMessageToUnreal({
    action: 'TELEPORT_TO_SPOT',
    brandingSpotId: ueIdentifier,
  });
};

export const blinkSpotInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  ueIdentifier: string,
) => {
  sendMessageToUnreal({
    action: 'BLINK_SPOT',
    brandingSpotId: ueIdentifier,
  });
};

export const teleportToTriggerInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  triggerName: string,
) => {
  sendMessageToUnreal({
    action: 'TELEPORT_TO_TRIGGER',
    triggerName,
  });
};

export const blinkTriggerInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  triggerName: string,
) => {
  sendMessageToUnreal({
    action: 'BLINK_TRIGGER',
    triggerName,
  });
};
